/**
 * Pure toast queue: at most {@link TOAST_MAX_VISIBLE} are on screen; extras
 * wait in FIFO order and pop in when a visible slot clears.
 */

export const TOAST_MAX_VISIBLE = 3;
export const TOAST_DEFAULT_DURATION_MS = 5000;

export type ToastDismissReason = "timeout" | "dismiss" | "action";

export interface ToastAction {
  label: string;
  onAction: () => void;
}

export interface ToastInput {
  id?: string;
  title: string;
  description?: string;
  /** Auto-dismiss delay. `0` keeps the toast until dismissed. */
  durationMs?: number;
  action?: ToastAction;
  /** Called when the toast leaves without the action (timeout or dismiss). */
  onCommit?: () => void | Promise<void>;
}

export interface ToastRecord {
  id: string;
  title: string;
  description?: string;
  durationMs: number;
  action?: ToastAction;
  onCommit?: () => void | Promise<void>;
  createdAt: number;
}

export interface ToastState {
  /** Newest first. */
  visible: ToastRecord[];
  /** Oldest waiting first. */
  queued: ToastRecord[];
}

let toastSeq = 0;

export function createToastId(): string {
  toastSeq += 1;
  return `toast-${toastSeq}`;
}

export function emptyToastState(): ToastState {
  return { visible: [], queued: [] };
}

export function toToastRecord(input: ToastInput, now = Date.now()): ToastRecord {
  return {
    id: input.id ?? createToastId(),
    title: input.title,
    description: input.description,
    durationMs: input.durationMs ?? TOAST_DEFAULT_DURATION_MS,
    action: input.action,
    onCommit: input.onCommit,
    createdAt: now,
  };
}

export function enqueueToast(
  state: ToastState,
  toast: ToastRecord,
  maxVisible = TOAST_MAX_VISIBLE,
): ToastState {
  if (state.visible.some((item) => item.id === toast.id)) {
    return {
      ...state,
      visible: state.visible.map((item) => (item.id === toast.id ? toast : item)),
    };
  }
  if (state.queued.some((item) => item.id === toast.id)) {
    return {
      ...state,
      queued: state.queued.map((item) => (item.id === toast.id ? toast : item)),
    };
  }
  if (state.visible.length < maxVisible) {
    return { visible: [toast, ...state.visible], queued: state.queued };
  }
  return { visible: state.visible, queued: [...state.queued, toast] };
}

export function dismissToast(
  state: ToastState,
  id: string,
  maxVisible = TOAST_MAX_VISIBLE,
): {
  state: ToastState;
  dismissed: ToastRecord | undefined;
  promoted: ToastRecord | undefined;
} {
  const visibleIdx = state.visible.findIndex((item) => item.id === id);
  if (visibleIdx >= 0) {
    const dismissed = state.visible[visibleIdx];
    const remaining = state.visible.filter((item) => item.id !== id);
    if (state.queued.length > 0 && remaining.length < maxVisible) {
      const [promoted, ...queued] = state.queued;
      return {
        state: { visible: [promoted, ...remaining], queued },
        dismissed,
        promoted,
      };
    }
    return {
      state: { visible: remaining, queued: state.queued },
      dismissed,
      promoted: undefined,
    };
  }

  const queuedIdx = state.queued.findIndex((item) => item.id === id);
  if (queuedIdx >= 0) {
    return {
      state: {
        visible: state.visible,
        queued: state.queued.filter((item) => item.id !== id),
      },
      dismissed: state.queued[queuedIdx],
      promoted: undefined,
    };
  }

  return { state, dismissed: undefined, promoted: undefined };
}
